import { faEllipsis, faFile } from '@fortawesome/pro-regular-svg-icons';
import type { ColumnDef } from '@tanstack/react-table';
import {
	flexRender,
	getCoreRowModel,
	getPaginationRowModel,
	useReactTable,
} from '@tanstack/react-table';
import {
	getInvoiceExcel,
	getInvoicePdf,
	getInvoiceUBL,
	type InvoiceDTO,
	useGetShipperInvoicesSuspense,
} from '@uturn/api/v1';
import { downloadFile, formatPrice } from '@uturn/portal/utils';
import { formatDate } from '@uturn/utilities/browser';
import {
	Button,
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
	Icon,
	PaginationCompact,
	sonner,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@uturn/ui-kit';
import type { TFunction } from 'i18next';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OverviewEmptyState } from './empty-state';

const columns = (
	t: TFunction<'translation', undefined>,
): ColumnDef<InvoiceDTO>[] => [
	{
		accessorKey: 'invoiceNumber',
		header: t('pages.invoices.invoice_number', 'Invoice number').toString(),
	},
	{
		accessorFn: (row) =>
			formatPrice(row.amount ?? 0, row.currency?.code ?? 'EUR'),
		header: t('general.amount', 'Amount').toString(),
	},
	{
		accessorFn: (row) => formatPrice(row.vat ?? 0, row.currency?.code ?? 'EUR'),
		header: t('general.VAT', 'VAT').toString(),
	},
	{
		accessorFn: (row) =>
			formatPrice(row.totalAmount ?? 0, row.currency?.code ?? 'EUR'),
		header: t('general.total_amount', 'Total amount').toString(),
	},
	{
		accessorFn: (row) =>
			row.invoiceDateUtc ? formatDate(row.invoiceDateUtc) : '',
		header: t('pages.invoices.invoice_date', 'Invoice date').toString(),
	},
	{
		id: 'download',
		cell: (cell) => {
			const row = cell.row.original;
			const responseType = 'blob';

			const handleDownloadClick = async (extension: 'pdf' | 'xlsx' | 'xml') => {
				if (!row.invoiceNumber) {
					return sonner.error(
						t(
							'pages.invoices.invoice_number.not_found',
							'Invoice number not found',
						),
					);
				}

				// FIXME: TECH-694 Fix filename underscores issue.
				// const invoiceFile = `${t('pages.invoices.invoice', 'Invoice')}-${row.invoiceNumber}.${extension}`; // Invoice___________________________-216006597.pdf
				const invoiceFile = `Invoice-${row.invoiceNumber}.${extension}`;

				switch (extension) {
					case 'pdf':
						downloadFile(
							getInvoicePdf(row.invoiceNumber, { responseType }),
							invoiceFile,
						);
						break;
					case 'xlsx':
						downloadFile(
							getInvoiceExcel(row.invoiceNumber, { responseType }),
							invoiceFile,
						);
						break;
					case 'xml':
						downloadFile(
							getInvoiceUBL(row.invoiceNumber, { responseType }),
							invoiceFile,
						);
						break;
					default:
						break;
				}
			};

			return (
				<div className="flex justify-end gap-3 pr-3.5">
					<Button onClick={() => handleDownloadClick('pdf')} variant="outline">
						<Icon icon={faFile} className="mr-1.5" />
						{t('pages.invoices.download', 'Download {{type}}', {
							type: 'PDF',
						})}
					</Button>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant="outline">
								<Icon icon={faEllipsis} />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent>
							<DropdownMenuItem onClick={() => handleDownloadClick('xlsx')}>
								{t('pages.invoices.dropdown.details', 'Details (Excel)')}
							</DropdownMenuItem>
							<DropdownMenuItem onClick={() => handleDownloadClick('xml')}>
								{t('pages.invoices.dropdown.e-invoice', 'E-invoice (UBL)')}
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
			);
		},
	},
];

const PAGE_SIZE = 20;

export function InvoicesTable() {
	const [page, setPage] = useState(0);

	const { data: shipperInvoices } = useGetShipperInvoicesSuspense(
		{
			page,
			size: PAGE_SIZE,
		} as any,
		{
			query: {
				select: (data) => data.data,
			},
		},
	);

	const totalPages = useMemo(() => {
		return Math.ceil(shipperInvoices.totalElements / shipperInvoices.pageSize);
	}, []);

	const { t } = useTranslation();

	const table = useReactTable({
		data: shipperInvoices.data,
		columns: columns(t),
		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		pageCount: totalPages,
		manualPagination: true,
		// biome-ignore lint/suspicious/noExplicitAny: Unknown function signature.
		onPaginationChange: (newPagination: any) => {
			setPage(newPagination(page).pageIndex);
		},
	});

	if (!shipperInvoices.data.length) {
		return <OverviewEmptyState />;
	}

	return (
		<>
			<div className="rounded-md border">
				<Table>
					<TableHeader>
						{table.getHeaderGroups().map((headerGroup) => (
							<TableRow key={headerGroup.id}>
								{headerGroup.headers.map((header) => {
									return (
										<TableHead key={header.id}>
											{header.isPlaceholder
												? null
												: flexRender(
														header.column.columnDef.header,
														header.getContext(),
													)}
										</TableHead>
									);
								})}
							</TableRow>
						))}
					</TableHeader>
					<TableBody>
						{table.getRowModel().rows && table.getRowModel().rows.length > 0 ? (
							table.getRowModel().rows.map((row) => (
								<TableRow
									key={row.id}
									data-state={row.getIsSelected() && 'selected'}
								>
									{row.getVisibleCells().map((cell) => (
										<TableCell key={cell.id}>
											{flexRender(
												cell.column.columnDef.cell,
												cell.getContext(),
											)}
										</TableCell>
									))}
								</TableRow>
							))
						) : (
							<TableRow>
								<TableCell
									colSpan={columns.length}
									className="h-24 text-center"
								>
									No results.
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</div>

			<PaginationCompact
				page={page}
				setPage={setPage}
				totalPages={totalPages}
			/>
		</>
	);
}
